import React, {Suspense, useEffect} from "react";
import HomepageLayout from "../components/page_layouts/HomepageLayout";
import "../utility/i18n";
import ErrorPage from "../components/page_layouts/ErrorPage";
import absoluteUrl from "next-absolute-url";
import HtmlHeadContent from "../components/layouts/head/HtmlHeadContent";
import {useThemeContext} from "../theming/ThemeContext";
import BlankPageLayout from "../components/page_layouts/BlankPageLayout";
import {
    generateDownloadUrl,
    getQueryParam,
    getShortUrlRootSlug,
    resolveRootSegmentByHost,
    setCsrfCookie
} from "../utility/navigationUtil";
import Analytics from "../extenions/googleTagManager/Analytics";
import settings, {
    DEFAULT_GTM_ID,
    PARAMETER_CAMPAIGN_ID,
    PARAMETER_CHANNEL_ID,
    PARAMETER_TEASER_CONTACT_EMAIL,
    SETTING_GTM_ID, SETTING_VWO_ID,
} from "./config/settings";
import CCOComment from "../components/fundamentals/CCOComment";
import {useSessionContext} from "../context/SessionContext";
import {PageProvider} from "../context/PageContext";
import {isBlankPage, isPageWithHiddenNavi, isWimmelbild} from "../components/page_layouts/pageLayoutsUtils";
import {GO_LINK, PORTAL_HP_URL, STIFTUNG_HOHENROTH_HP} from "../common/constants/routes";
import {MemoryCache} from 'memory-cache-node';
import {checkForFreshness, getNavi, getPage, getPageById, getSites} from "../extenions/queryCache/CacheLayer";
import {isNumber} from "../utility/numberUtil";
import {PreviewProvider} from "../context/PreviewContext";
import VWO from "../extenions/vwo/VWO";
import {SosLocation} from "../../packages/graphql-layer/src";
import {getSelfUrl} from "./go";

export const CACHE_COOKIE_TS_NAME = 'rae-ts';
export const COOKIE_PATH = { path: '/' };

interface Props {
    siteId: string,
    rootUrl: string,
    page?: any
    navigation: any,
    CAEGlobalSettings: any,
    currentURL: string,
    segment: string,
    content?: any,
    csrfToken: string,
    referrer?: string | URL,
    gtmId: string,
    vwoId: string,
    comment: string,
    pageSettings?: any,
    isEmpty?: boolean,
    preview?: boolean,
    credentials?: any
}

// Diese Variable wird von allen Threads genutzt!
const globalMemory = global as unknown as { memoryCache: MemoryCache<string, any> };

// Singleton hier!!!
export const getMemoryCache = async () => {
    if (globalMemory.memoryCache) {
        return globalMemory.memoryCache;
    }
    const itemsExpirationCheckIntervalInSecs = 60;
    const maxItemCount = 1000000;
    const memoryCache = new MemoryCache<string, any>(itemsExpirationCheckIntervalInSecs, maxItemCount);
    globalMemory.memoryCache = memoryCache;
    return memoryCache;
}

function PageContainer(props: Props) {
    const themeContext = useThemeContext();
    const session = useSessionContext();

    useEffect(() => {
            themeContext.setTheme(props.segment)
            if (typeof window !== "undefined") {
                const search = window.location.search;
                const channelId = getQueryParam(search, PARAMETER_CHANNEL_ID);
                if (channelId) {
                    session.addToSession(PARAMETER_CHANNEL_ID, channelId);
                }
                const campaignId = getQueryParam(search, PARAMETER_CAMPAIGN_ID);
                if (campaignId) {
                    session.addToSession(PARAMETER_CAMPAIGN_ID, campaignId);
                }
                const contactEmail = getQueryParam(search, PARAMETER_TEASER_CONTACT_EMAIL);
                if (contactEmail) {
                    session.addToSession(PARAMETER_TEASER_CONTACT_EMAIL, contactEmail);
                }
            }
        },
        // eslint-disable-next-line
        []);

    useEffect(() => {
    }, [props.segment, themeContext, session]);

    if (!props.isEmpty && props.page) {
        const isHideNavi = props.page && isPageWithHiddenNavi(props.page);
        let taxonomies = [];
        if (props.page && props.page.sosDonationPurposeTaxonomy && props.page.sosDonationPurposeTaxonomy.length > 0) {
            taxonomies.push(props.page.sosDonationPurposeTaxonomy[0].value)
        }
        if (props.navigation && props.navigation.content.site.root && props.navigation.content.site.root.sosLocationTaxonomy && props.navigation.content.site.root.sosLocationTaxonomy.length > 0) {
            let location = props.navigation.content.site.root.sosLocationTaxonomy[0] as SosLocation;
            taxonomies.push(location.shortName || location.value);
        }

        return <PageProvider page={props.page} siteId={props.siteId} rootUrl={props.rootUrl} taxonomies={taxonomies}>
            <PreviewProvider isPreview={!!props.preview}>
            <HtmlHeadContent CAEGlobalSettings={props.CAEGlobalSettings} page={props.page} content={props.content}
                             currentURL={props.currentURL} isPreview={props.preview}/>
            <CCOComment text={props.comment}/>
            <Suspense>
                <Analytics gtmId={props.gtmId} referrer={props.referrer}/>
                <VWO vwoId={props.vwoId}/>
            </Suspense>
            {isBlankPage(props.page) || isWimmelbild(props.page) ?
                <BlankPageLayout page={props.page} navigation={props.navigation}
                                 CAEGlobalSettings={props.CAEGlobalSettings}
                                 currentURL={props.currentURL}
                                 content={props.content}
                                 credentials={props.credentials}
                                 csrfToken={props.csrfToken}/>
                : <HomepageLayout isHideNavi={isHideNavi}
                                  page={props.page}
                                  navigation={props.navigation}
                                  CAEGlobalSettings={props.CAEGlobalSettings}
                                  currentURL={props.currentURL}
                                  content={props.content}
                                  csrfToken={props.csrfToken}
                                  pageSettings={props.pageSettings}
                                  credentials={props.credentials}/>}
            </PreviewProvider>
        </PageProvider>
    } else {
        return <ErrorPage statusCode={404} rootUrl={props.rootUrl}/>
    }
}

function getRootSlug(slug: any, segmentByHost: string) {
    // Wenn segmentByHost NICHT leer ist und NICHT portal, dann nimm das als RootSegment
    if (segmentByHost !== "" && segmentByHost !== "portal") {
        return segmentByHost
    } else {
        // Sonst:
        if (slug && slug.length > 0) {
            // WENN erster slug === portal und zweiter slug gesetzt und segmentByHost !== "" (also urlShortening aktiv)
            // DANN versuchen wir es mit zweiten slug als rootSegment
            // Falls es damit nicht klappt, wird es anschließend noch mal gesetzt. Siehe: if (!siteId && isShortUrl) {
            if (slug.length > 1 && slug[0] === "portal" && segmentByHost !== "") {
                return slug[1];
            } else {
                // Sonst: nimm erster slug als rootSegment
                return slug[0];
            }
        } else {
            return "";
        }
    }
}

function getPathFromSlug(slug: any, siteRootElement: string) {
    // Slug ist gesetzt?
    if (slug && slug.length > 0) {
        // Wenn der Slug > 1 ist, dann kann es sein, dass es sich um eine Redirectseite handelt.
        // Für Redirectseiten gilt:
        // slug[0]: 'portal'
        // slug[1]: 'siteRootElement'
        // Falls also slug[1] === siteRootElement, dann gib als pfad alles, ab slug[1] zurück
        if (slug.length > 1 && slug[1] === siteRootElement) {
            let path = "";
            for (let i = 1; i < slug.length; i++) {
                path = path + slug[i] + "/"
            }
            return path.substring(0, path.length - 1);
            // Falls slug[1] !== siteRootElement
        } else {
            // Wenn slug[0] !== siteRootElement ist, müssen wir das siteRootElement vorne anfügen
            if (slug[0] !== siteRootElement) {
                return siteRootElement + "/" + slug.join("/");
                // Sonst gibt slug als Pfad zurück
            } else {
                return slug.join("/");
            }
        }
    } else {
        return siteRootElement;
    }
}

async function checkForServerDataFreshness(req: any, res: any) {
    const memoryCache = await getMemoryCache();
    const hlsCacheVersionAfterCheck = await checkForFreshness(memoryCache);
    return {memoryCache, hlsCacheVersionAfterCheck};
}

function getAppInfos() {
    const dcVersion = `${process.env.DOCTYPE_VERSION}`;
    const gqlEndpoint = `${process.env.GRAPHQL_ENDPOINT}`;
    return "dcVersion: " + dcVersion + " / gqlEndpoint: " + gqlEndpoint;
}

function checkPreview(req: any) {
    const preview = getQueryParam(req.url, "preview");
    return (preview && preview === 'true');
}

async function getGoLinkUrl(slug: any, query: any) {
    if (slug && slug.length > 1 && slug[1] === GO_LINK) {
        const pid = query.cmpid;
        if (pid && pid !== '') {
            return await getSelfUrl(pid);
        }
    }
    return null;
}

/**
 * Hier wird der Content anhand des Pfades oder der ID geladen. Zuerst wird aber die zugehörige SiteId (CMS-Mandant) ermittelt.
 * Es gibt hier sehr viele unterschiedliche UseCases, die teils nur beim aktivierten UrlShortening greifen:
 *
 * UseCase 1: Normale Site (bsp. testament-schreiben.com):
 * Wenn das UrlShortening aktiv ist, wird hier das erste Pfadsegement anhand des HostHeaders ermittelt.
 *
 * UseCase 2: Normale Portalseite
 * Wenn das UrlShortening aktiv ist, wird hier das erste Pfadsegement anhand des HostHeaders ermittelt.
 *
 * UseCase 3: Einrichtungsseite
 * Wenn das UrlShortening aktiv ist, wird hier das erste Pfadsegement als SiteRootSegement verwendet.
 * Gibt es für dieses SiteRootSegment eine SiteId, dann wird der Content für diese Site mit siteRootSegment === pfad[0] geladen
 *
 * UseCase 4: Redirectseiten als MiniSites
 * Hier hat der slug ein Vorangestelltes portal als erstes Segment. Daher wird versucht mit slug[1] eine siteId zu finden.
 * Gibt es diese siteId, dann ist es eine Redirectseite. Als Pfad wird nun alles ab slug[1] verwendet und slug[0] wird verworfen
 *
 * UseCase5:
 * Ist das UrlShortening (das gilt für alle Preview-Systeme) deaktiviert, dann greift die Regel, dass slug[0] === portalRootSegment
 *
 * UseCase6:
 * Es macht einen Unterschied, ob man mit dem ersten Request über den Reverse-Proxy hier reinkommt,
 * oder ob es sich um ein Routing innerhalb von NextJs handelt.
 * Beim internen Routing ist slug[0] === zweites Pfadsegment zum Contentlookup. Also beispielsweise 'spenden'.
 * Das erste Pfadsegment muss nun durch das Hostsegment ermittelt werden! Das braucht es aber nur, bei aktiven urlShortening (s.o.)
 */
export async function getServerSideProps(context: any) {
    const {req, res, query} = context;
    // HLS Cache Version aus Cookie lesen
    const {memoryCache, hlsCacheVersionAfterCheck} = await checkForServerDataFreshness(req, res);
    // Informationen über App abfragen
    const comment = getAppInfos();
    // Metainformationen der App sammeln
    let csrfToken = '';
    const referrer = req.headers.referer || req.headers.referrer || null;
    const isPreview = checkPreview(req);

    // HOST-Header auslesen und root-url-segment anhand des headers identifizieren
    const hostHeader = req.headers['host'];
    let segmentByHost = resolveRootSegmentByHost(hostHeader);
    const slug = context.query.slug ? context.query.slug : [];

    // Enable Go-Links (Short-URLs) für Produkte
    const goUrl = await getGoLinkUrl(slug, query);
    // Wenn goUrl gefunden wurde, dann redirecten wir auf die Seite
    if (goUrl && goUrl !== '') {
        return {
            redirect: {
                destination: goUrl, permanent: false,
            },
        }
    }

    // Wenn segmentByHost == 'de' und erstes Pfadsegment == 'stiftung-hohenroth',
    // dann ist es eine Ausnahme. In dem Fall gibt es kein UrlShortening und wir laden den Inhalt für die Site 'stiftung-hohenroth'
    if (slug && slug.length > 0 && slug[0] === STIFTUNG_HOHENROTH_HP) {
        segmentByHost = '';
    }
    // Wenn segmentByHost nicht leer ist, handelt es sich um eine RAE hinter einem ReverseProxy mit UrlShortening
    const isShortUrl = segmentByHost !== '';
    // Das rootSegment zum Auffinden der siteId ist nicht immer das erste Pfadsegment (also slug[0])
    // Denn, wenn das System hinter dem Reverse-Proxy steht, dann fehlt diese Information
    let siteRootSegment = getRootSlug(slug, segmentByHost);
    // Alle Seiten/Mandanten von CoreMedia laden
    const sitesData = await getSites(memoryCache);
    // siteId anhand des siteRootSegment identifizieren
    let siteId = findSiteIdBySegment(sitesData, siteRootSegment)
    // Wenn die siteId nicht gefunden werden konnte und es sich um eine shortUrl handelt, dann ist es eine portal-URL
    // SegmentByHost sollte dann 'portal' enthalten
    if (!siteId && isShortUrl) {
        siteId = findSiteIdBySegment(sitesData, segmentByHost)
        // Wenn die siteId nun da ist, dann ist das siteRootSegment == segmentByHost == 'portal'
        if (siteId) {
            siteRootSegment = segmentByHost
        }
    }
    // Wenn es sich um eine shortUrl handelt, dann schauen wir, ob es sich nicht um eine Einrichtung handelt,
    // sondern eine der bekannten ROOT-Segmente ist. Falls das der Fall ist, wird für das clientseitige URL-Building das
    // die rootUrl auf "/" gesetzt. Sonst ist es "/siteRootSegment"
    const rootUrl = isShortUrl ? "/" + getShortUrlRootSlug(siteRootSegment) : "/" + siteRootSegment;
    // Wenn es eine siteId gibt, können wir den Content anhand des Pfades laden
    if (siteId) {
        // Navigation laden
        const navData = await getNavi(memoryCache, siteId);
        // SETTINGS für Site-Context laden
        const caeSettings = navData?.content?.site?.root?.settings
        if (!caeSettings) {
            console.warn("CaeSettings is null")
        }
        const gtmId = settings(caeSettings, SETTING_GTM_ID, DEFAULT_GTM_ID);
        const vwoId = settings(caeSettings, SETTING_VWO_ID);
        // CMS-Lookup-Pfad für den aktuellen Pfad erstellen
        const path = getPathFromSlug(slug, siteRootSegment)
        const pageData = await getPage(memoryCache, path);
        let currentURL = `${absoluteUrl(context.req).origin}/${path}`

        // Kein content für diesen CMS-Lookup-Pfad. Also schauen wir, ob es eine ID im slug gibt
        if (!pageData || !pageData.content || !pageData.content.pageByPath) {
            const id = path.substring(path.lastIndexOf('-') + 1);
            const idIsANumber = isNumber(id);
            // Daten nur laden, wenn es sich um eine ID/Zahl handelt
            const contentData = idIsANumber ? await getPageById(memoryCache, id) : null;
            let isEmpty = false;
            let pageByPathData = {};
            // Wenn es für die ID ebenfalls keinen content gibt, dann geht es weiter mit der 404-Seite
            if (!contentData || !contentData.content || !contentData.content.content) {
                isEmpty = true;
            } else {
                // Wenn bisher alles da ist, dann lade parent-path
                const parentPath = path.substring(0, path.lastIndexOf('/'));
                const pdata = idIsANumber ? await getPage(memoryCache, parentPath) : {};
                pageByPathData = pdata?.content?.pageByPath;
            }
            if (isEmpty || !pageByPathData) {
                res.statusCode = 404;
            }
            // Inhalt wurde ueber ID geladen. Setze csrf-token
            csrfToken = setCsrfCookie(req, res);
            return {
                props: {
                    siteId: siteId,
                    rootUrl: rootUrl,
                    navigation: navData,
                    content: contentData,
                    page: pageByPathData,
                    CAEGlobalSettings: caeSettings,
                    currentURL: currentURL,
                    segment: siteRootSegment,
                    csrfToken: csrfToken,
                    referrer: referrer,
                    gtmId: gtmId,
                    vwoId: vwoId,
                    comment: comment,
                    isEmpty: isEmpty,
                    preview: isPreview
                } as Props
            }

        } else {
            if (!pageData) {
                console.warn("pageData is null")
            } else {
                if (!pageData.content) {
                    console.warn("pageData's content is null")
                }
            }
            // Wenn es sich um eine RedirectPage handelt, wird der Redirect eingeleitet, bevor es ins Rendering geht
            if (pageData.content.pageByPath.type === "SOSRedirectPage") {
                const page = pageData.content.pageByPath;
                const linkTarget = page.linkTarget?.[0]?.teaserTarget;
                const isTargetDownload = linkTarget?.type === 'CMDownload';
                const redirectUrl = isTargetDownload ? linkTarget?.selfUrl || page.redirectUrl : page.redirectUrl;
                const pageSettings = pageData.content.pageByPath!.pageSettings;
                const redirectType = pageSettings['redirect-type'] || 301;
                const target = isTargetDownload
                    ? generateDownloadUrl(linkTarget.id, linkTarget.title)
                    : redirectUrl || PORTAL_HP_URL;

                return {
                    redirect: {
                        destination: target,
                        statusCode: redirectType
                    },
                }
            } else {
                // Hier geht es mit den Seitendaten ins Rendering
                if (!pageData!.content!.pageByPath) {
                    res.statusCode = 404;
                }
                // Inhalt wurde ueber url-pfad geladen. Setze csrf-token
                csrfToken = setCsrfCookie(req, res);
                return {
                    props: {
                        credentials: {
                            friendlyCaptchaId: process.env.FRIENDLY_CAPTCHA_ID,
                        },
                        siteId: siteId,
                        rootUrl: rootUrl,
                        navigation: navData,
                        page: pageData.content.pageByPath,
                        CAEGlobalSettings: caeSettings,
                        currentURL: currentURL,
                        segment: siteRootSegment,
                        csrfToken: csrfToken,
                        referrer: referrer,
                        gtmId: gtmId,
                        vwoId: vwoId,
                        comment: comment,
                        pageSettings: pageData.content.pageByPath!.pageSettings,
                        preview: isPreview
                    } as Props
                }
            }
        }
    } else {
        // Wenn es keine siteId gibt, dann nimm portal als root
        res.statusCode = 404;
        return {
            props: {
                navigation: null,
                page: null,
                currentURL: '',
                segment: 'portal',
                csrfToken: csrfToken,
                isEmpty: true
            } as Props
        }
    }
}



export function findSiteIdBySegment(sitesData: any, siteRootSegment: string): string | undefined | null {
    if (siteRootSegment === "") {
        return undefined;
    } else {
        const site = Array.of(...sitesData.content.sites).find(s => s.root && s.root.segment === siteRootSegment)
        return site ? site.id : null;
    }
}

export default PageContainer